// composables/usePoolaOffers.js
//
// Data layer for the Poola buy screen's offer cards + the money-moving claim.
// Cursor-paginated read on jsonApiClient; plain-JSON write on apiClient. Read
// the real contract — don't guess:
//   ../backend/_bmad-output/planning-artifacts/poola-pwa-fe-contract-2026-06-30.md Section 3,Section 4,Section 5
//   ../backend/app/JsonApi/V1/PoolaOffers/PoolaOfferSchema.php
//   ../backend/app/JsonApi/V1/PoolaOffers/PoolaOfferCollectionQuery.php
//   ../backend/app/Http/Controllers/Api/V1/Poola/PoolaOfferClaimController.php
//
// Mirrors useSupplierWall.js (cursor accumulation + dedupe-by-id into a
// pinia-orm repo). Two deliberate differences:
//   1. The claim is SERVER-CONFIRMED, not optimistic (D4) — it's a final money
//      move + an irreversible PoolShare burn, so we patch stock/balances ONLY
//      after HTTP 200, and throw a typed error on failure for the sheet to
//      branch on (errors[0].code). No optimistic flash.
//   2. The suppliers filter is CLIENT-SIDE (D8) — the endpoint exposes only
//      filter[deal], so the rail is derived in memory over loaded offers.
import { useRepo } from 'pinia-orm';
import { computed, getCurrentScope, onScopeDispose, ref, unref } from 'vue';
import { POOLA_OFFERS_PAGE_LIMIT, POOLA_SUPPLIER_ALL_KEY } from '@/constants/poola.js';
import { apiClient, jsonApiClient } from '@/httpConfig/config/axios.js';
import PoolaOffer from '@/models/commerce/PoolaOffer.js';
import Post from '@/models/posts/Post.js';
import { isRealtimeConnected } from '@/realtime/echo.js';
import { newIdempotencyKey } from '@/utils/idempotency.js';
import { readPageMeta } from '@/utils/jsonApiCursor.js';
import { POOLA_BAND_KEYS, sortAndGroupOffers } from '@/utils/poolaOfferSort.js';

export const OFFERS_ENDPOINT = '/poola-offers';

// Cap-unlock skew re-poll (BE→FE brief 2026-07-13-poola-cap-increase.md): the
// contract is "client countdown → refetch" with NO push, so if the device clock
// hits the cap-increase target a hair early the authoritative read can still show
// the old (un-bumped) cap. We then re-poll a BOUNDED number of times, ~2s apart,
// until the bump is observed on the wire (or the button goes live) — never a tight
// loop. capIncreaseAt is validated BEFORE endAt, so the timer resolves in-deal.
const CAP_REFETCH_MAX_TRIES = 5;
const CAP_REFETCH_INTERVAL_MS = 2000;

/**
 * Typed claim failure. The sheet branches on `.code` (the stable
 * errors[0].code contract key) — `.code === null` marks an INDETERMINATE
 * outcome (network / timeout / 5xx) that should replay the same idempotency
 * key, vs a deterministic 4xx that rotates it.
 */
export class PoolaClaimError extends Error {
  constructor({ code = null, status = null, cause = null } = {}) {
    super(code || 'poola-claim-failed');
    this.name = 'PoolaClaimError';
    this.code = code; // stable errors[0].code, or null when indeterminate
    this.status = status; // HTTP status, or null
    this.cause = cause;
  }
}

/** Read a value / ref / getter uniformly (dealId may arrive async via a ref). */
function read(value) {
  return typeof value === 'function' ? value() : unref(value);
}

/**
 * Broadcast `timestamp` → a comparable number (or null). We only ever COMPARE
 * these for ordering within one page session, so the unit (epoch ms/s, or an ISO
 * string) is irrelevant as long as the backend emits it consistently.
 */
function toTs(value) {
  if (value == null) return null;
  const n = typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * The viewer's DERIVED completion reward for `offer` (cents), or null when any of
 * the server's null-rules fails. Pure + ABSOLUTE — Σ the viewer's PURCHASED
 * PoolShare = poolshareCents × myClaimedQty (1:1 بولشير→بولكريديت, BE-confirmed),
 * the exact figure the server's `myPoolcreditEarnedCents` reconciles to on the next
 * collection read. `complete` is passed in because the completion signal differs
 * per caller: a PENDING realtime stock patch (applyClaimBroadcast) vs the
 * already-committed own-claim state (claim()). Being pure + write-once makes it
 * idempotent, so whichever of the two paths runs second lights the per-user chip.
 *
 * Returns null (→ no per-user chip; the card's collective/social line carries it)
 * unless ALL hold, matching the DELIVERED contract exactly
 * (../backend/_bmad-output/sync/briefs/2026-07-13-poola-completion-poolcredit-DELIVERED.md):
 *   - the offer is complete;
 *   - it has a supplier (standalone → server grants nothing → null);
 *   - the viewer holds ≥1 PoolShare-spending PURCHASED claim (non-claimant / all-cash → null);
 *   - the amount isn't already known (write-once → never overwrites the server value);
 *   - the socket is CONNECTED (on the polling path the collection read is authoritative).
 */
function deriveViewerReward(offer, complete) {
  if (
    complete &&
    offer.myPoolcreditEarnedCents == null &&
    (offer.myClaimedQty ?? 0) > 0 &&
    offer.supplierId != null &&
    offer.poolshareCents > 0 &&
    isRealtimeConnected()
  ) {
    return offer.poolshareCents * offer.myClaimedQty;
  }
  return null;
}

/**
 * usePoolaOffers — offers list + facets + claim for one run.
 *
 * @param {string | import('vue').Ref<string> | (() => string)} dealId
 *   The current run id (= filter[deal]). REQUIRED at fetch time — a missing id
 *   short-circuits the fetch (the contract 422s on a blank filter[deal]).
 */
export function usePoolaOffers(dealId) {
  const repo = useRepo(PoolaOffer);
  const postRepo = useRepo(Post);

  // Server (cursor) order is authoritative — keep the id sequence explicitly
  // and derive the rendered list from the ORM so the server-confirmed claim
  // patch re-flows automatically.
  const offerIds = ref([]);
  const nextCursor = ref(null);
  const hasMore = ref(true);
  const loading = ref(false); // first-load / refresh
  const isLoadingMore = ref(false); // pagination
  const error = ref(null);
  const activeSupplier = ref(POOLA_SUPPLIER_ALL_KEY);
  let abortController = null;

  // A host that is gone must not load. A full load FLUSHES the whole PoolaOffer
  // table (fetchPage), and that table is shared: the Poola page and the
  // last-Poola page each mount one of these, one after the other. A slow answer
  // that lands after its page was removed would flush the table under the page
  // that replaced it and empty its list (seen in the browser:
  // tests/e2e/poola-last-run.spec.js). So on dispose: abort the request in
  // flight (it then rejects before the flush), drop a pending cap re-poll (no
  // host ever cancelled it, and it would start a fresh load later), and refuse
  // any load asked for afterwards. Guarded: the unit tests call this composable
  // outside any scope.
  //
  // NOT covered: the few hundred milliseconds of a page transition, when the
  // leaving page is still mounted next to the new one. Closing that needs a
  // flush that only removes the rows its own instance loaded — a decision for
  // the owner (deferred-work.md).
  let disposed = false;
  if (getCurrentScope()) {
    onScopeDispose(() => {
      disposed = true;
      clearCapRefetch();
      if (abortController) abortController.abort();
    });
  }

  // Cap-unlock skew re-poll state. `capRefetchTimer` is the one pending setTimeout
  // (null = none). `capRepollInternal` is true ONLY during a cycle-owned
  // loadInitial() so that internal read doesn't cancel its own cycle — while the
  // cycle is idle between polls the flag is false, so a user-driven loadInitial /
  // PTR / flip clears the pending re-poll and supersedes it. `capRepollCount`
  // bounds the tries.
  let capRefetchTimer = null;
  let capRepollInternal = false;
  let capRepollCount = 0;

  // Per-offer last-applied broadcast timestamp — a monotonic guard so a REORDERED
  // (older) `.PoolaOfferClaimed` frame can't overwrite newer live stock. Cleared
  // whenever the list is fully re-fetched (loadInitial/PTR/reconnect flush the
  // repo), so it stays in step with the offers it guards and never needs pruning.
  // Frames arrive in send-order over one WebSocket channel and stock is absolute,
  // so this is defence against the (near-impossible) reorder — not load-bearing.
  const lastClaimTs = new Map();

  // Full loaded set (facets derive from this); `offers` is the filtered view.
  const offersAll = computed(() => offerIds.value.map((id) => repo.find(id)).filter(Boolean));

  // Supplier read-only path: offers where isMine is server-confirmed true.
  const myOffers = computed(() => offersAll.value.filter((o) => o.isMine === true));

  // Member "حجوزاتي" path: offers this member currently holds (myClaimedQty > 0).
  // Sibling to `myOffers` (the supplier isMine subset). A computed over offersAll,
  // so it re-derives automatically on every server-confirmed claim patch (which
  // bumps myClaimedQty) and on each loadMore page. `?? 0` keeps a null/undefined
  // count out of the list rather than throwing on the comparison.
  const myClaims = computed(() => offersAll.value.filter((o) => (o.myClaimedQty ?? 0) > 0));

  // ── Supplier facets (client-side, D8) ──────────────────────────────────────
  // GUARDRAIL: the offers endpoint exposes ONLY filter[deal] — there is no
  // filter[supplier] (PoolaOfferCollectionQuery.php). So the rail is derived in
  // memory and re-derives on every loadMore (this is a computed over offersAll).
  // v1 keys by supplierName — the offer carries no stable supplierId yet (see
  // ../backend/_bmad-output/sync/briefs/2026-06-30-poola-supplier-facets.md).
  // STANDALONE offers (supplierName == null) carry no chip and live only under
  // «all».
  const suppliers = computed(() => {
    const byKey = new Map();
    for (const offer of offersAll.value) {
      const name = offer.supplierName;
      // STANDALONE (null) OR a blank/whitespace name → «all» only, never a chip
      // (a blank name would render a keyless ghost chip).
      if (name == null || String(name).trim() === '') continue;
      const existing = byKey.get(name);
      if (existing) {
        existing.count += 1;
      } else {
        byKey.set(name, {
          key: name,
          name,
          avatarUrl: offer.supplierAvatarUrl ?? null,
          isPlatform: Boolean(offer.supplierIsPlatform),
          count: 1,
        });
      }
    }
    return [
      {
        key: POOLA_SUPPLIER_ALL_KEY,
        name: null,
        avatarUrl: null,
        isPlatform: false,
        count: offersAll.value.length,
      },
      ...byKey.values(),
    ];
  });

  // Rendered list = full set filtered by the active chip — a VIEW, no refetch.
  const offers = computed(() => {
    if (activeSupplier.value === POOLA_SUPPLIER_ALL_KEY) return offersAll.value;
    return offersAll.value.filter((offer) => offer.supplierName === activeSupplier.value);
  });

  function setSupplier(key) {
    const next = key ?? POOLA_SUPPLIER_ALL_KEY;
    // Re-tapping the already-active chip is a no-op — skip the rebuild so it can't
    // reflow the frozen order (pre-P4-A this was a cached no-op; resort() would
    // otherwise re-sort every card whose live stock drifted since the last sort).
    if (next === activeSupplier.value) return;
    activeSupplier.value = next;
    resort(); // facet changed → rebuild the banded order for the new view
  }

  // ── Completion sort + section bands (P4-A) ──────────────────────────────────
  // The member buy surface (PoolaRun) renders offers nearest-to-finish first,
  // grouped under «قربوا يكملوا» / «اكتملوا» bands. That order is FROZEN here into
  // an id list per band and rebuilt ONLY on discrete events (loadInitial / refresh
  // / loadMore / setSupplier) — deliberately NOT on a realtime `.PoolaOfferClaimed`
  // stock patch, which would otherwise re-sort every few seconds and yank a card
  // out from under a scrolling thumb. `sections` maps the frozen ids back to LIVE
  // repo entities, so live stock still updates in place; only the SLOT is frozen.
  // (offers / offersAll above stay server-order + live for the supplier/prospect
  // runs, which don't band — this is a member-surface-only view.)
  const bandedIds = ref({ nearComplete: [], regular: [], completed: [] });

  function matchesSupplier(offer) {
    return (
      activeSupplier.value === POOLA_SUPPLIER_ALL_KEY || offer.supplierName === activeSupplier.value
    );
  }

  function toBandIds(bands) {
    return {
      nearComplete: bands.nearComplete.map((o) => o.id),
      regular: bands.regular.map((o) => o.id),
      completed: bands.completed.map((o) => o.id),
    };
  }

  // FULL rebuild — recomputes every card's band + slot over the whole filtered
  // set. Called on loadInitial / refresh / setSupplier ONLY (discrete, non-scroll
  // events). Never on a realtime patch (would jump cards) and never on loadMore
  // (would reorder cards the user just scrolled past — see appendPage).
  function resort() {
    bandedIds.value = toBandIds(sortAndGroupOffers(offersAll.value.filter(matchesSupplier)));
  }

  // loadMore path — APPEND the freshly-fetched page into the frozen bands after
  // the existing ids, so nothing the user already scrolled past moves. The new
  // page is sorted only among ITSELF; a full re-sort here would reorder existing
  // cards. The next PTR/refresh calls resort() and re-sorts the whole set cleanly.
  function appendPage(pageOffers) {
    const grouped = sortAndGroupOffers(pageOffers.filter(matchesSupplier));
    const seen = new Set(POOLA_BAND_KEYS.flatMap((k) => bandedIds.value[k]));
    const next = { nearComplete: [], regular: [], completed: [] };
    for (const key of POOLA_BAND_KEYS) {
      const add = grouped[key].map((o) => o.id).filter((id) => !seen.has(id));
      next[key] = [...bandedIds.value[key], ...add];
    }
    bandedIds.value = next;
  }

  // Frozen, banded, LIVE-content view for PoolaRun. Order comes from bandedIds
  // (frozen at the last resort/append); entities come from the repo (live), so a
  // `.PoolaOfferClaimed` patch updates «باقي {n}» in place without moving the
  // card. Empty bands are kept — the caller renders a header only for a non-empty
  // group. `.filter(Boolean)` drops any id whose entity was flushed out from under
  // the frozen order (e.g. a refresh racing the map).
  const sections = computed(() =>
    POOLA_BAND_KEYS.map((key) => ({
      key,
      offers: bandedIds.value[key].map((id) => repo.find(id)).filter(Boolean),
    })),
  );

  function buildQueryParams(cursor = null) {
    // filter[deal] is REQUIRED (omit ⇒ 422). NEVER send sort / include (both
    // 422) or a sparse fields[poola-offers] — a sparse read makes `canClaim`
    // resolve to null (PoolaOfferSchema.php). Keep this object to exactly these
    // keys.
    const params = {
      'filter[deal]': read(dealId),
      'page[limit]': POOLA_OFFERS_PAGE_LIMIT,
    };
    if (cursor) params['page[after]'] = cursor;
    return params;
  }

  async function fetchPage(reset = false) {
    if (disposed) return;
    if (loading.value || (isLoadingMore.value && !reset)) return;

    const id = read(dealId);
    if (id == null || id === '') return; // no run id → nothing to fetch

    // Own a per-request controller so a superseded fetch (e.g. an onFlip-driven
    // refresh aborting an in-flight loadMore) can't clobber the live request's
    // loading flags / cursor in its finally — only the latest request resets.
    const controller = new AbortController();
    if (abortController) abortController.abort();
    abortController = controller;

    try {
      if (reset) {
        loading.value = true;
        error.value = null;
      } else {
        isLoadingMore.value = true;
      }

      const cursor = reset ? null : nextCursor.value;
      const res = await jsonApiClient.get(OFFERS_ENDPOINT, {
        params: buildQueryParams(cursor),
        signal: controller.signal,
      });

      // The host went away while this was in flight and the abort did not reject
      // it (the answer was already on its way in): do not touch the shared table.
      if (disposed) return;

      const doc = res?.data ?? {};
      const rows = doc.data ?? [];
      const { cursor: nc, hasMore: more } = readPageMeta(doc);

      if (reset) {
        repo.flush();
        offerIds.value = [];
        lastClaimTs.clear(); // fresh authoritative stock → drop stale monotonic anchors
        activeSupplier.value = POOLA_SUPPLIER_ALL_KEY; // a stale chip can't strand the view
      }

      if (rows.length) {
        // Thread the auto-included post + media into the Posts repo so
        // PlatformShare can resolve the post via
        //   useRepo(Post).with('media').find(postId).
        // Mirrors usePostsFeed's postRepo.save() — same model, same deserializer.
        // Does NOT flush the Posts repo (shared with the feed); postRepo.save
        // upserts by id, so re-fetching the same offer is idempotent.
        const includedPosts = rows.map((o) => o.post).filter(Boolean);
        if (includedPosts.length) postRepo.save(includedPosts);
        // Stamp postId flat on each row so the PoolaOffer ORM save carries it.
        // Standalone offers (no post) → postId = null → hasShareablePost = false.
        rows.forEach((o) => {
          o.postId = o.post?.id ?? null;
          // Per-viewer completion reward — DELIVERED by the backend as a derived
          // resource field (2026-07-13-poola-completion-poolcredit-DELIVERED.md):
          // present (cents) on a completed, supplier-backed offer the viewer
          // claimed, null otherwise (ACTIVE / standalone / non-claimant). Coerce
          // undefined → null so a card never feature-detects a raw undefined and
          // the live P6-A patch (applyClaimBroadcast) has a clean null baseline.
          o.myPoolcreditEarnedCents = o.myPoolcreditEarnedCents ?? null;
          // Effective cap AFTER a scheduled cap-increase (BE→FE brief
          // 2026-07-13-poola-cap-increase.md). null = uncapped offer OR the
          // attribute wasn't emitted; coerce undefined → null so the cap-unlock
          // countdown predicate never feature-detects a raw undefined. Do NOT
          // default to 0 — null is a real "uncapped" value here, not "no pieces".
          o.upcomingMaxPerMember = o.upcomingMaxPerMember ?? null;
        });

        repo.save(rows);
        const seen = new Set(offerIds.value);
        const fresh = rows.map((row) => row.id).filter((rid) => !seen.has(rid)); // de-dupe across overlapping cursors
        offerIds.value = [...offerIds.value, ...fresh];

        // loadMore only: fold the new page into the FROZEN bands (append, no
        // reorder). A reset rebuilds the whole banded order below instead.
        if (!reset && fresh.length) {
          appendPage(fresh.map((id) => repo.find(id)).filter(Boolean));
        }
      }

      // loadInitial / refresh (incl. an empty result) → (re)build the full sorted,
      // banded order for the fresh set. resort() reads offersAll (now reflecting
      // the reset ids) + the active supplier facet.
      if (reset) resort();

      nextCursor.value = nc;
      hasMore.value = more;
    } catch (err) {
      if (err?.name === 'AbortError' || err?.name === 'CanceledError') return;
      if (reset) error.value = err; // a load-more failure leaves the list intact
      // eslint-disable-next-line no-console
      console.error('[usePoolaOffers] fetch error:', err);
    } finally {
      // Only the active (latest) request clears shared state — a superseded
      // request that lost the abort race must not reset the live one's flags.
      if (abortController === controller) {
        loading.value = false;
        isLoadingMore.value = false;
        abortController = null;
      }
    }
  }

  function loadInitial() {
    // A USER-driven fresh read (mount / PTR / flip) supersedes any pending
    // cap-unlock re-poll so the two can't double-fetch. Guard on capRepollInternal
    // so the cycle's OWN internal read doesn't cancel its own cycle.
    if (!capRepollInternal) clearCapRefetch();
    nextCursor.value = null;
    hasMore.value = true;
    return fetchPage(true);
  }

  // ── Cap-unlock skew re-poll (BE→FE 2026-07-13-poola-cap-increase.md) ─────────
  function clearCapRefetch() {
    if (capRefetchTimer !== null) {
      clearTimeout(capRefetchTimer);
      capRefetchTimer = null;
    }
    capRepollCount = 0;
  }

  // Cancel a pending cap-unlock re-poll — the host calls this on teardown (this
  // composable registers no lifecycle hooks). loadInitial()/refresh() also clear
  // it, so a normal fresh read already supersedes an in-flight cycle.
  function cancelCapRefetch() {
    clearCapRefetch();
  }

  // A cycle-owned authoritative read; the internal flag keeps loadInitial from
  // cancelling the very cycle that drives it.
  async function capInternalLoad() {
    capRepollInternal = true;
    try {
      await loadInitial();
    } catch {
      // loadInitial surfaces fetch failures via `error`; keep polling within budget.
    } finally {
      capRepollInternal = false;
    }
  }

  // True once the wire reflects the cap-increase for THIS member on some CAPPED
  // offer: either the effective cap caught up to the upcoming cap (bump observed)
  // or the button went live. Uncapped offers (maxPerMember == null) never qualify
  // — the bump imposes no ceiling on them, so their canClaim is irrelevant here.
  function capBumpObserved() {
    return offersAll.value.some(
      (o) =>
        o &&
        o.maxPerMember != null &&
        (o.maxPerMember === o.upcomingMaxPerMember || o.canClaim === true),
    );
  }

  // Schedule the next bounded re-poll attempt. On budget exhaustion we stop and
  // rely on PTR / the next natural refetch — never a tight loop.
  function scheduleCapRepoll() {
    if (disposed) return; // the host is gone: nothing left to re-poll for
    if (capRepollCount >= CAP_REFETCH_MAX_TRIES) {
      clearCapRefetch(); // budget spent
      return;
    }
    capRepollCount += 1;
    capRefetchTimer = setTimeout(runCapRepoll, CAP_REFETCH_INTERVAL_MS);
  }

  async function runCapRepoll() {
    capRefetchTimer = null;
    await capInternalLoad();
    if (capBumpObserved()) {
      clearCapRefetch();
      return;
    }
    scheduleCapRepoll();
  }

  /**
   * Re-read offers when the deal's cap-increase countdown elapses. There is NO
   * push (the locked "client countdown → refetch" contract), so:
   *   1. do an authoritative fresh read (loadInitial);
   *   2. if the bump isn't visible yet (device-clock skew — the read beat the
   *      server boundary), re-poll up to CAP_REFETCH_MAX_TRIES times,
   *      CAP_REFETCH_INTERVAL_MS apart, stopping as soon as capBumpObserved().
   * The button stays driven off the refetched `canClaim` (never hard-enabled).
   */
  async function refetchForCapIncrease() {
    clearCapRefetch(); // supersede any in-flight cycle so we never run two loops
    await capInternalLoad(); // authoritative read at the boundary
    if (capBumpObserved()) return;
    scheduleCapRepoll();
  }

  function loadMore() {
    if (!hasMore.value || loading.value || isLoadingMore.value) return Promise.resolve();
    return fetchPage(false);
  }

  async function refresh(done) {
    try {
      await loadInitial();
    } finally {
      if (typeof done === 'function') done();
    }
  }

  /**
   * Claim `qty` pieces of an offer — the final money move (D4, server-confirmed).
   * POST /poola-offers/{offer}/claim via apiClient (plain JSON, snake_case body).
   *
   * The CALLER (the claim sheet) owns the idempotency key's attempt/replay
   * lifecycle and passes it in; we default-generate one only as a safety net so
   * a bare call never sends an empty key.
   *
   * @param {object|string} offer - the PoolaOffer (or its id).
   * @param {number} qty - pieces to claim (≥1; the sheet bounds this).
   * @param {string} [idempotencyKey] - one UUID per attempt; replayed verbatim on retry.
   * @returns {Promise<{ purchase: object|null, balances: object|null }>} on HTTP 200.
   * @throws {PoolaClaimError} carrying errors[0].code + http status on failure.
   */
  async function claim(offer, qty, idempotencyKey) {
    const offerId = offer && typeof offer === 'object' ? offer.id : offer;

    // Coerce + guard qty to a positive integer BEFORE the money move. The sheet
    // bounds it, but a malformed qty (e.g. a string from a stepper) must never
    // reach the server OR the local patch: `-` numeric-coerces while `+` string-
    // concatenates, so a string qty would corrupt myClaimedQty/piecesClaimed.
    const n = Number(qty);
    if (!Number.isInteger(n) || n < 1) {
      throw new PoolaClaimError({ code: 'invalid-qty', status: null });
    }

    const key = idempotencyKey ?? newIdempotencyKey();

    let res;
    try {
      res = await apiClient.post(`${OFFERS_ENDPOINT}/${offerId}/claim`, {
        qty: n,
        idempotency_key: key,
      });
    } catch (err) {
      throw new PoolaClaimError({
        code: err?.response?.data?.errors?.[0]?.code ?? null,
        status: err?.response?.status ?? null,
        cause: err,
      });
    }

    // apiClient does NOT transform — res.data is the raw plain-JSON body.
    const purchase = res?.data?.data?.attributes ?? null;
    const balances = res?.data?.meta?.balances ?? null;

    // Server-confirmed LOCAL patch — only reached on HTTP 200.
    const current = repo.find(offerId);
    if (current) {
      // myClaimedQty is per-viewer — the `.PoolaOfferClaimed` broadcast never
      // carries it, so claim() is its SOLE writer and always patches it (+n once).
      const patch = { id: offerId, myClaimedQty: (current.myClaimedQty ?? 0) + n };
      // Stock (pieces*) is club-wide + AUTHORITATIVE via the absolute
      // `.PoolaOfferClaimed` broadcast. When the socket is CONNECTED that frame
      // will arrive and own these fields, so patching them RELATIVELY here too
      // would double-apply on the broadcast-arrives-first ordering (the frame has
      // no viewer identity, so it can't be suppressed): e.g. the absolute frame
      // sets remaining=85, then this relative patch reads 85 and writes 85−n — an
      // understated count that can falsely gate maxQty / show SOLD OUT. So patch
      // stock ONLY as the FALLBACK when no broadcast is coming (tunnel/offline);
      // when connected, defer to the broadcast (a brief stale-high remaining is
      // benign — the server is the real gate — and self-heals on the next frame).
      if (!isRealtimeConnected()) {
        patch.piecesRemaining = Math.max(0, (current.piecesRemaining ?? 0) - n);
        patch.piecesClaimed = (current.piecesClaimed ?? 0) + n;
      }
      repo.save(patch);

      // If the member's OWN claim was the completing one and the sold-out
      // broadcast landed BEFORE this HTTP 200 (so applyClaimBroadcast saw
      // myClaimedQty=0 and skipped the reward), derive it now that myClaimedQty is
      // patched. Write-once/absolute, so whichever of the two paths runs second is
      // the one that lights the per-user chip — either arrival order is covered.
      const patched = repo.find(offerId);
      const reward = deriveViewerReward(patched, patched.isComplete);
      if (reward != null) repo.save({ id: offerId, myPoolcreditEarnedCents: reward });
    }

    return { purchase, balances };
  }

  /**
   * Apply a `.PoolaOfferClaimed` realtime frame to the loaded offer — the
   * club-wide live-stock signal (ANY member's claim/refund, not just this
   * viewer's). Patches the SAME public stock fields the local claim writes, minus
   * `myClaimedQty`: that is per-viewer and is NOT carried by the broadcast, so it
   * is deliberately left untouched (the own-claim path in claim() owns it).
   *
   * Uses the event's ABSOLUTE counts (never a delta), so a duplicate frame is
   * idempotent; a monotonic timestamp guard drops a reordered older frame. Offers
   * not currently loaded are ignored (no card to patch; the next page/refetch
   * carries authoritative values).
   *
   * @param {object} event `{ offer_id, pieces_claimed, pieces_remaining, status, timestamp }`
   * @returns {'claim'|'refund'|'noop'|'stale'|'unknown'} classification for the
   *   caller's "claiming now" rolling window — 'claim' = a genuine new claim.
   */
  function applyClaimBroadcast(event) {
    const offerId = event?.offer_id;
    if (offerId == null) return 'noop';

    // Monotonic guard: a strictly-older frame than the last one we applied for
    // this offer is a reorder — drop it (absolute sets are otherwise idempotent).
    const ts = toTs(event?.timestamp);
    const lastTs = lastClaimTs.get(offerId);
    if (ts != null && lastTs != null && ts < lastTs) return 'stale';
    if (ts != null) lastClaimTs.set(offerId, ts);

    const current = repo.find(offerId);
    const nextClaimed = Number(event?.pieces_claimed);

    // Classify BEFORE patching (delta vs the on-card value) so the live pill counts
    // real claims, not refunds. Unloaded offer → 'unknown' (real activity somewhere
    // in the run, but not on a card we hold).
    let kind = 'unknown';
    if (current && Number.isFinite(nextClaimed)) {
      const prevClaimed = current.piecesClaimed ?? 0;
      kind = nextClaimed > prevClaimed ? 'claim' : nextClaimed < prevClaimed ? 'refund' : 'noop';
    }

    if (current) {
      const wasComplete = current.isComplete;
      const patch = { id: offerId };
      if (Number.isFinite(nextClaimed)) patch.piecesClaimed = nextClaimed;
      const nextRemaining = Number(event?.pieces_remaining);
      if (Number.isFinite(nextRemaining)) patch.piecesRemaining = Math.max(0, nextRemaining);
      const nextStatus = Number(event?.status);
      if (Number.isFinite(nextStatus)) patch.status = nextStatus;

      // ── Per-viewer completion reward (P6-A) ────────────────────────────────
      // The `.PoolaOfferClaimed` frame carries NO reward amount:
      // myPoolcreditEarnedCents is PER-VIEWER and cannot ride the shared
      // `PoolaDeal.{id}` channel (each claimant earned a different amount), so the
      // backend delivers it as a resource field, not a broadcast payload — BE→FE
      // brief 2026-07-13-poola-completion-poolcredit-DELIVERED.md. So when THIS
      // frame completes an offer the member holds a claim on, deriveViewerReward
      // computes it locally (poolshareCents × myClaimedQty) so the per-user
      // «رجعلك {amount}» chip lights up at the completion instant. willComplete is
      // pieces-based to match PoolaOffer.isComplete (which drives the celebration),
      // so the chip and the burst stay consistent within one tick.
      const willRemaining = patch.piecesRemaining ?? current.piecesRemaining ?? 0;
      const willComplete = (current.piecesTotal ?? 0) > 0 && willRemaining <= 0;
      if (wasComplete && !willComplete) {
        // Reopen (a refund freed a piece: SOLD_OUT → ACTIVE). Drop the
        // completion-moment reward so it reads null while reopened — matching the
        // server (DELIVERED brief Section Reopen caveat) — and a later re-completion
        // re-derives against the CURRENT claim count rather than reusing a stale one.
        patch.myPoolcreditEarnedCents = null;
      } else {
        // Idempotent, no double-apply: deriveViewerReward is a pure ABSOLUTE
        // function written only while currently-null, so a duplicate/replayed frame
        // recomputes the same value and never overwrites an authoritative server one.
        const reward = deriveViewerReward(current, willComplete);
        if (reward != null) patch.myPoolcreditEarnedCents = reward;
      }

      repo.save(patch);
    }

    return kind;
  }

  return {
    offers, // filtered view (by activeSupplier), server-order + live
    sections, // frozen, banded, live-content view for the member buy surface (P4-A)
    resort, // re-apply the sort/bands NOW — call only at safe (non-scroll) moments
    offersAll, // full loaded set (for facets / debug)
    myOffers, // supplier path: offers where isMine === true
    myClaims, // member path: offers this member holds (myClaimedQty > 0)
    suppliers,
    activeSupplier,
    setSupplier,
    hasMore,
    isLoadingMore,
    loading,
    error,
    loadInitial,
    loadMore,
    refresh,
    refetchForCapIncrease, // cap-unlock countdown elapsed → authoritative read + bounded skew re-poll
    cancelCapRefetch, // host teardown hook: cancel a pending cap-unlock re-poll
    claim,
    applyClaimBroadcast, // realtime `.PoolaOfferClaimed` → live stock patch
  };
}
